import React, { useState, useEffect, useRef } from 'react'
import i18n from 'live/services/i18n'
import Select from 'react-select'
import SaveButton from 'live/components/forms/ui/button-save'
import styled, { css } from 'styled-components'
import theme from 'live/services/theme'
import api from '../api'
import { uniq, isEqual } from 'lodash'
import globalNotification from 'live/services/global-notification'
import DateService from 'live/services/date'
import {
    Accordion,
    AccordionTitle,
    AccordionContent,
    useAccordionSingle,
    useAccordionMultiple,
    AtomicAccordionMultiple
} from 'live/components/ui/Accordion'

interface InterventionType {
    id: string
    sections: string[]
    name: string
    type: string
    status: string
    begin: number
    end: number
}
interface TemplateRow {
    program_name: string
    section_name: string
    activity_name: string
    sub_activity_name: string
    sub_activity_priority: string
    start_rules: string
    include_rules: string
    end_rules: string
    includestart_rules: string
    includeend_rules: string
    includestartend_rules: string
    prev_sub_activity: string
    next_sub_activity: string
    after_all_sub_activity: string
    before_all_sub_activity: string
}

const extractNumber = (str) => str.match(/^\D*(\d+(?:\.\d+)?)(S?)/gm)?.[0]
const extracSectionName = (sectionName) => !!extractNumber(sectionName) ? (extractNumber(sectionName) + ' in') : sectionName

export default ({ ...props }) => {
    const [saved, setSaved] = useState<boolean>(false)
    const [loading, setLoading] = useState<boolean>(true)
    const [configForm, setConfigForm] = useState<Map<String, String>>({})
    const [template, setTemplate] = useState<TemplateRow[]>([])
    const [interventions, setInterventions] = useState<InterventionType[]>([])
    const [programsOptions, setProgramsOptions] = useState<Object[]>([])
    const [selectedInterventionId, setSelectedInterventionId] = useState<String>(null)
    const [errors, setErrors] = useState({})
    const wellId = props?.selected?.asset?.id
    // const interventionId = props?.selected?.asset?.state?.intervention?.data?.id
    const company_type = props?.selected?.asset?.type ?? ""
    const company = company_type.includes('offshore') ? 'offshore' : company_type.includes('onshore') ? 'onshore' : null
    const savedConfigRef = useRef(null)
    const saveDisabled = !savedConfigRef.current || !configForm || isEqual(savedConfigRef.current, configForm)

    // // console.log('savedConfigRef.current', savedConfigRef.current, 'configForm', configForm, 'equal', isEqual(savedConfigRef.current, configForm))
    // console.log('props', props, 'company', company, 'wellId', wellId)

    function onSubmit() {
        api.saveConfigByWellId(wellId, configForm).then(e => {
            globalNotification.success(i18n('ILT Programs configuration successfully saved'))
        })
    }

    useEffect(() => {
        const controller = new AbortController()
        const signal = controller.signal
        api.getTemplate(signal, company)
            .then(res => {
                if (res.ok) {
                    res.json().then(data => setTemplate(data))
                }
            })
    }, [company])

    useEffect(() => {
        const controller = new AbortController()
        const signal = controller.signal
        api.getConfigByWellId(signal, wellId)
            .then(res => {
                if (res.ok) {
                    res.json().then(data => {
                        setConfigForm(data)
                        savedConfigRef.current = data
                    })
                }
            })
    }, [wellId])

    useEffect(() => {
        const controller = new AbortController()
        const signal = controller.signal

        async function getInterventions() {
            const intervArray = []
            const interventions = await api.getInterventions(signal, wellId)
                .then(res => {
                    if (res.ok) {
                        return res.json().then(data => data?.interventions)
                    }
                })

            for (let i = 0; i < interventions.length; i++) {
                const interv = interventions[i].intervention;

                const sections = await api.getSections(signal, interv.id)
                    .then(res => {
                        if (res.ok) {
                            try {
                                return res.json().then(data => data?.sections).catch(data => [])
                            } catch (error) {
                                console.error(error)
                            }
                        }
                    })

                interv.sections = uniq(sections?.map(s => extracSectionName(s.name)))
                intervArray[i] = interv
            }

            setInterventions(intervArray)
            setLoading(false)
        }

        getInterventions()

    }, [wellId])

    function onCancel() {
        // // console.log('onCancel')
    }

    useEffect(() => {
        // console.log('config form change', configForm)
    }, [configForm])

    useEffect(() => {
        const wellId = props?.selected?.asset?.id
        const company = props?.selected?.asset?.attributes?.description?.type.indexOf('offshore') >= 0 ? 'offshore' : 'onshore'
        // console.log('props change', props, wellId, company)
    }, [props])

    useEffect(() => {
        // console.log('template change', template)
        setProgramsOptions(uniq(template.map(r => r.program_name)).map(e => ({ value: e, label: e })))
    }, [template])

    return <ContainerBody>
        <div className="config-container">
            {interventions.length > 0 ? interventions.map(interv => {
                const beginDate = !!interv.begin ? DateService.getMoment(interv.begin).format('YYYY-MM-DD HH:mm:ss') : "-"
                const endDate = !!interv.end ? DateService.getMoment(interv.end).format('YYYY-MM-DD HH:mm:ss') : "-"
                const intervConfig = configForm[interv.id] ?? {}
                const sections = interv.sections
                return <AccordionContainer className="container">
                    <AccordionTitle className="row" active={selectedInterventionId == interv.id} toggleActive={() => setSelectedInterventionId(selectedInterventionId != interv.id ? interv.id : null)}>
                        <div className="intervention-name">{interv.name ?? "-"}</div>
                        <div className="intervention-type">{interv.type ?? "-"}</div>
                        <div className="intervention-status">{interv.status ?? "-"}</div>
                        <div className="intervention-time-range">{beginDate} to {endDate}</div>
                    </AccordionTitle>
                    <AccordionContent className="sections-content" active={selectedInterventionId == interv.id}>
                        {sections.length > 0 ? sections.map(sectionStr => {
                            const section = extracSectionName(sectionStr)
                            return (
                                <div className='section-container'>
                                    <div className='dropdown-container'>
                                        <Label>{i18n(sectionStr)} Program:</Label>
                                        <Select options={programsOptions} onChange={e => setConfigForm({ ...configForm, [interv.id]: { ...intervConfig, [section]: e?.value } })}
                                            value={intervConfig[section]} />
                                    </div>
                                    <div className='activities-container'>
                                        {intervConfig[section] && <Label>{i18n("Activities")}:</Label>}
                                        {intervConfig[section] && (<ul>
                                            {uniq(template
                                                .filter(r => r.program_name == intervConfig[section] && extracSectionName(r.section_name) == section)
                                                .map(r => r.activity_name))
                                                .map(activity => <li>{activity}</li>)
                                            }
                                        </ul>)}
                                    </div>
                                    <div className='subactivities-container'>
                                        {intervConfig[section] && <Label>{i18n("Sub Activities")}:</Label>}
                                        {intervConfig[section] && (<ul>
                                            {uniq(template
                                                .filter(r => r.program_name == intervConfig[section] && extracSectionName(r.section_name) == section)
                                                .map(r => r.sub_activity_name))
                                                .map(subActivity => <li>{subActivity}</li>)
                                            }
                                        </ul>)}
                                    </div>
                                </div>
                            )
                        }) : !loading ? <div className='no-sections'>No sections configured</div> : null}
                    </AccordionContent>
                </AccordionContainer>
            }) : 'No interventions configured'}
        </div>
        {interventions.length > 0 && <div className="form-actions">
            <SaveButton
                className="btn-lg btn-bold mr-5"
                disabled={saveDisabled}
                message={i18n('Confgiuration saved successfully')}
                onClick={onSubmit}
            />
            <span>{i18n('or')} </span>

            <a onClick={onCancel} className="cancel">
                {' '}
                {i18n('cancel')}
            </a>
        </div>}
    </ContainerBody>
}

const AccordionContainer = styled.div`
    padding-bottom: 10px;

    .row {
        margin-left: 0px !important;
        margin-right: 0px !important;
        border-bottom: solid 1px lightgray;
        cursor: pointer;
        font-weight: 700;

        .intervention-name {
            min-width: 200px;
            max-width: 200px;
        }
        .intervention-type {
            min-width: 100px;
            max-width: 100px;
        }
        .intervention-status {
            min-width: 100px;
            max-width: 100px;
        }
        .intervention-time-range {
            min-width: 300px;
            max-width: 300px;
        }
    }
`

const ContainerBody = styled.div`
    height: 100%;
    width: 100%;
    top: 0px;
    left: 0px;
    position: absolute;

    .sections-content {
        padding: 10px;
        border-bottom: solid 1px lightgray;
    }

    .config-container {
        height: calc(100% - 8px);
        padding-top: 20px;
        padding-left: 20px;
        padding-bottom: 20px;
        overflow-y: auto;
    }

    .section-container:last-child {
        border-bottom: none !important;
    }

    .no-sections {
        border-bottom: none !important;
    }

    .section-container {
        display: flex;
        min-width: 200px;
        border-bottom: solid 1px lightgray;
        padding: 15px 0px;
    }

    .dropdown-container {
        padding: 5px;
        margin-right: 20px;
        min-width: 250px;
    }

    .activities-container {
        padding: 5px;
        margin-right: 20px;
        min-width: 250px;
    }

    .subactivities-container {
        padding: 5px;
        margin-right: 20px;
        min-width: 250px;
    }

    .form-actions {
        padding: 20px 0px;
        border-top: solid 1px lightgray;
        padding-left: 20px;
    }

`

const Label = styled.span`
    display: flex;
    font-family: ${theme['font-semibold']};
    font-size: 13px;
    font-weight: 600;
    font-stretch: normal;
    font-style: normal;
    line-height: normal;
    letter-spacing: normal;
    color: var(--gray-800);
`